import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useCouponIntent } from '../context/CouponIntentContext';
import * as cartApi from '../api/endpoints/cart';
import * as configApi from '../api/endpoints/config';
import * as serviceContextApi from '../api/endpoints/serviceContext';
import { checkoutGeographyFor } from '../utils/checkoutGeography';
import useKeyedRequest from './useKeyedRequest';
import { cartKeyFor, normalizeCouponCode, quoteLinesFor } from './useCartQuote';

/**
 * Everything Checkout knows about delivering THIS cart to THIS address.
 *
 * The delivery address — not the browsing location — is the geography here:
 * each request pins it (`deliveryGeography`), so the browsing location the
 * storefront uses is never read or changed. The backend resolves the serving
 * outlet from it; nothing here names one.
 *
 *   address geography ─► service context (can anyone deliver here?)
 *                          └─ serviceable ─► quote  (cart + coupon intent, priced here)
 *                                        └─► slots  (that outlet's delivery times)
 *
 * Each stage is keyed by exactly its inputs and answered only for them:
 *   service   address geography
 *   quote     address geography + cart lines + submitted coupon
 *   slots     address geography
 * so choosing another address drops the previous address's context, quote,
 * token and slots in the same render, and a late answer for it is discarded.
 * This quote is Checkout's own: the Cart's quote (browsing location, possibly
 * older) is never reused, even when the two geographies are the same.
 *
 * serviceStatus: no_address | pincode_missing | coordinates_missing |
 *                resolving | serviceable | unserviceable | error
 * quoteStatus / slotsStatus: idle | loading | ready | error
 */
const useCheckoutQuote = (address) => {
  const { authStatus } = useAuth();
  const { items } = useCart();
  const { couponCode: couponIntent } = useCouponIntent();

  const { geography, key: geographyKey, problem } = checkoutGeographyFor(address);
  const signedIn = authStatus === 'authenticated';

  const service = useKeyedRequest(
    signedIn && geographyKey ? `service#${geographyKey}` : null,
    () => serviceContextApi.getServiceContext(geography)
  );
  const serviceable = service.status === 'ready' && service.data?.serviceable === true;
  let serviceStatus;
  if (problem) serviceStatus = problem;
  else if (service.status === 'error') serviceStatus = 'error';
  else if (service.status === 'ready') serviceStatus = serviceable ? 'serviceable' : 'unserviceable';
  else serviceStatus = 'resolving';

  const lines = useMemo(() => quoteLinesFor(items), [items]);
  const cartKey = useMemo(() => cartKeyFor(lines), [lines]);
  const couponCode = normalizeCouponCode(couponIntent);

  const quoteKey = serviceable && lines.length > 0 ? `${geographyKey}#${cartKey}#${couponCode || ''}` : null;
  const quote = useKeyedRequest(quoteKey, () => cartApi.quoteCart(lines, { couponCode, deliveryGeography: geography }));

  const slotsKey = serviceable ? geographyKey : null;
  const slots = useKeyedRequest(slotsKey, () => configApi.getDeliverySlots({ deliveryGeography: geography }));

  const current = quote.data;
  return {
    geography,
    geographyKey,
    serviceStatus,
    serviceError: service.error,
    retryService: service.refresh,

    couponCode,
    quoteKey,
    quoteStatus: quote.status,
    quote: current,
    quoteError: quote.error,
    retryQuote: quote.refresh,
    // Install the fresh quote a placement conflict (QUOTE_CHANGED) returned —
    // only for the exact inputs it was priced for; false means those inputs
    // have moved on, and the current key's own quote stands (or is loading).
    replaceQuote: quote.replace,
    // What order placement will compare against (Phase 9): the backend's own
    // token and total for this address, cart and coupon — opaque, never derived.
    quoteToken: current?.quoteToken || null,
    expectedGrandTotal: typeof current?.grandTotal === 'number' ? current.grandTotal : null,
    binding: current ? current.binding === true : null,

    slotsKey,
    slotsStatus: slots.status,
    slots: slots.data,
    slotsError: slots.error,
    retrySlots: slots.refresh,
  };
};

export default useCheckoutQuote;
